import React, { useMemo, useState } from 'react';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import AddHomeIcon from '@mui/icons-material/AddHome';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import UpgradeIcon from '@mui/icons-material/Upgrade';

import { Device, UserRoles } from '../interfaces';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectSelectedOnboardDeviceTableRows } from '../store/devices.reducer';
import { loadActivationSticks } from '../store/activationSticks.reducer';
import { loadOnboardDevicesHardwareConfigurations } from '../store/onboardDeviceHardwareConfigurations.reducer';
import { loadOnboardDevicesOnlinePeers } from '../store/onboardDevicesOnlinePeers.reducer';
import { loadSystemConfigurations } from '../store/systemConfigurations.reducer';
import { useCognitoUser } from '../hooks/useCognitoUser';
import { hasAllowedRole } from '../utils/hasAllowedRole';
import { exportToFile, exportToPdf, getSelectedDevices } from '../utils/onboardDevicesExport';
import { AttachOrganizationDialog } from './AttachOrganizationDialog';
import { CreateDevicesDialog } from './CreateDevicesDialog';
import { GetSecretsDialog } from './GetSecretsDialog';
import { RemoveDevicesDialog } from './RemoveDevicesDialog';
import { UpdateVersionDialog } from './UpdateVersionDialog';
import { UploadOnboardDeviceLogsDialog } from './UploadOnboardDeviceLogsDialog';

interface OnboardDevicesToolbarProps {
    devices: Device[];
    hasScannedFilter: boolean;
    scannedDevicesCount: number;
    onResetScannedDevices: () => void;
    onReloadOnboardDevices: () => void;
}

type OnboardDevicesDialog =
    | 'updateVersion'
    | 'getSecrets'
    | 'attachOrganization'
    | 'createDevices'
    | 'removeDevices'
    | 'uploadLogs';

export function OnboardDevicesToolbar({
    devices,
    hasScannedFilter,
    scannedDevicesCount,
    onResetScannedDevices,
    onReloadOnboardDevices,
}: OnboardDevicesToolbarProps) {
    const dispatch = useAppDispatch();
    const { userRoles } = useCognitoUser();
    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const [activeDialog, setActiveDialog] = useState<OnboardDevicesDialog | null>(null);

    const isAdmin = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, [UserRoles.ADMINS]);
    }, [userRoles]);

    const onReload = () => {
        onReloadOnboardDevices();
        dispatch(loadActivationSticks());
        dispatch(loadSystemConfigurations());
        dispatch(loadOnboardDevicesHardwareConfigurations());
        dispatch(loadOnboardDevicesOnlinePeers());
    };

    const closeDialog = () => setActiveDialog(null);

    const closeDialogAndReload = () => {
        closeDialog();
        onReloadOnboardDevices();
    };

    const onExportIds = () => {
        const selectedDevices = getSelectedDevices(selectedRows, devices);

        exportToFile(selectedDevices);
        exportToPdf(selectedDevices);
    };

    return (
        <>
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <Button
                    startIcon={<FilterAltOffIcon />}
                    disabled={!hasScannedFilter}
                    color="primary"
                    onClick={onResetScannedDevices}
                >
                    {hasScannedFilter
                        ? `Clear Scanned Devices (${scannedDevicesCount})`
                        : 'Clear Scanned Devices'}
                </Button>
                {isAdmin && (
                    <Button
                        startIcon={<UpgradeIcon />}
                        disabled={selectedRows.length === 0}
                        color="primary"
                        onClick={() => setActiveDialog('updateVersion')}
                    >
                        Change Version
                    </Button>
                )}
                <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                    Reload
                </Button>
                <Button
                    disabled={selectedRows.length !== 1}
                    startIcon={<KeyIcon />}
                    color="primary"
                    onClick={() => setActiveDialog('getSecrets')}
                >
                    Get Secrets
                </Button>
                {isAdmin && (
                    <Button
                        disabled={selectedRows.length === 0}
                        startIcon={<AddHomeIcon />}
                        color="primary"
                        onClick={() => setActiveDialog('attachOrganization')}
                    >
                        Attach Organization
                    </Button>
                )}
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    onClick={() => setActiveDialog('createDevices')}
                >
                    Create Devices
                </Button>
                <Button
                    disabled={selectedRows.length === 0}
                    startIcon={<RemoveIcon />}
                    color="primary"
                    onClick={() => setActiveDialog('removeDevices')}
                >
                    Remove Devices
                </Button>
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    disabled={selectedRows.length === 0}
                    onClick={onExportIds}
                >
                    Export Ids
                </Button>
                <Button
                    startIcon={<StickyNote2Icon />}
                    color="primary"
                    disabled={selectedRows.length !== 1}
                    onClick={() => setActiveDialog('uploadLogs')}
                >
                    Upload Logs
                </Button>
            </GridToolbarContainer>

            {activeDialog === 'updateVersion' && (
                <UpdateVersionDialog
                    selectedDeviceIds={selectedRows}
                    open
                    onClose={closeDialog}
                    onUpdateComplete={closeDialogAndReload}
                />
            )}
            {activeDialog === 'getSecrets' && (
                <GetSecretsDialog selectedDeviceId={selectedRows[0]} open onClose={closeDialog} />
            )}
            {activeDialog === 'attachOrganization' && (
                <AttachOrganizationDialog
                    selectedDeviceIds={selectedRows}
                    open
                    onClose={closeDialog}
                    onAttachComplete={closeDialogAndReload}
                />
            )}
            {activeDialog === 'createDevices' && (
                <CreateDevicesDialog open onClose={closeDialog} onCreationCompleted={closeDialogAndReload} />
            )}
            {activeDialog === 'removeDevices' && (
                <RemoveDevicesDialog
                    open
                    selectedDeviceIds={selectedRows}
                    onClose={closeDialog}
                    onRemovalCompleted={closeDialogAndReload}
                />
            )}
            {activeDialog === 'uploadLogs' && (
                <UploadOnboardDeviceLogsDialog
                    selectedDeviceId={selectedRows[0]}
                    open
                    onClose={closeDialog}
                />
            )}
        </>
    );
}
